import type { IntelligenceDraftRequest, IntelligenceSampler } from '@/types/onboarding';
import { INTELLIGENCE_SETUP_MAX_BYTES } from '@/lib/intelligence-mode';

const DRAFT_VERSION = 1;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_DRAFT_BYTES = INTELLIGENCE_SETUP_MAX_BYTES;

export interface SetupDraftStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export interface IdentitySetupDraft {
  name: string;
  website: string;
  attemptKey: string;
}

export interface IntelligenceSetupDraft {
  intelligenceMode?: IntelligenceSampler['intelligence_mode'];
  prompts: string[];
  promptRefs?: string[];
  promptGroups?: import('@/lib/prompt-topics/contract').SetupTopic[];
  pasteText: string;
  message: string;
  history: IntelligenceDraftRequest['history'];
  schedule: IntelligenceSampler['schedule'];
  sampleCount?: number;
  engines?: IntelligenceSampler['engines'];
  geoTarget?: IntelligenceSampler['geo_target'];
  method: 'paste' | 'upload' | 'ai';
  website: string;
}

export function setupDraftKey(
  userId: string,
  scope: string,
  kind: 'identity' | 'intelligence'
): string {
  return `attune:setup:v${DRAFT_VERSION}:${encodeURIComponent(userId)}:${kind}:${encodeURIComponent(scope)}`;
}

export function readSetupDraft<T>(
  storage: SetupDraftStorage | null,
  key: string,
  now = Date.now()
): T | null {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as { version: number; expiresAt: number; value: T };
    if (
      envelope.version !== DRAFT_VERSION ||
      !Number.isFinite(envelope.expiresAt) ||
      envelope.expiresAt <= now
    ) {
      storage?.removeItem(key);
      return null;
    }
    return envelope.value;
  } catch {
    return null;
  }
}

function writeDraft(storage: SetupDraftStorage | null, key: string, value: unknown): boolean {
  try {
    const raw = JSON.stringify({
      version: DRAFT_VERSION,
      expiresAt: Date.now() + DRAFT_TTL_MS,
      value,
    });
    if (new TextEncoder().encode(raw).length > MAX_DRAFT_BYTES) return false;
    storage?.setItem(key, raw);
    return Boolean(storage);
  } catch {
    return false;
  }
}

/** Explicit allowlists prevent provider credentials or File objects entering persisted setup. */
export function writeIdentityDraft(
  storage: SetupDraftStorage | null,
  key: string,
  draft: IdentitySetupDraft
): boolean {
  return writeDraft(storage, key, {
    name: draft.name,
    website: draft.website,
    attemptKey: draft.attemptKey,
  });
}

export function writeIntelligenceDraft(
  storage: SetupDraftStorage | null,
  key: string,
  draft: IntelligenceSetupDraft,
  savedWebsite = ''
): boolean {
  if (
    !draft.prompts.length &&
    !draft.pasteText &&
    !draft.message &&
    !draft.history.length &&
    draft.schedule.type === 'daily_4x' &&
    draft.sampleCount === undefined &&
    draft.website.trim() === savedWebsite.trim() &&
    !draft.engines &&
    !draft.geoTarget
  ) {
    clearSetupDraft(storage, key);
    return true;
  }
  return writeDraft(storage, key, {
    intelligenceMode: draft.intelligenceMode,
    prompts: draft.prompts,
    promptRefs: draft.promptRefs,
    promptGroups: draft.promptGroups,
    pasteText: draft.pasteText,
    message: draft.message,
    history: draft.history.map(({ role, content }) => ({ role, content })),
    schedule: {
      type: draft.schedule.type,
      ...(draft.schedule.day_of_week !== undefined
        ? { day_of_week: draft.schedule.day_of_week }
        : {}),
    },
    sampleCount: draft.sampleCount,
    engines: draft.engines,
    geoTarget: draft.geoTarget
      ? {
          city: draft.geoTarget.city,
          region: draft.geoTarget.region,
          country_code: draft.geoTarget.country_code,
          timezone: draft.geoTarget.timezone,
        }
      : undefined,
    method: draft.method,
    website: draft.website,
  });
}

export function clearSetupDraft(storage: SetupDraftStorage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* Storage is optional. */
  }
}

export function sessionDraftStorage(): SetupDraftStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function validateWorkspaceIdentity(name: string, website: string): string | null {
  if (!name.trim()) return 'Enter a workspace name.';
  if (!website.trim()) return null;
  try {
    const url = new URL(
      /^https?:\/\//i.test(website.trim()) ? website.trim() : `https://${website.trim()}`
    );
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      !url.hostname.includes('.') ||
      url.username ||
      url.password
    )
      return 'Enter a valid website, such as example.com.';
  } catch {
    return 'Enter a valid website, such as example.com.';
  }
  return null;
}

export function normalizeSetupWebsite(website: string): string {
  const value = website.trim();
  return !value || /^https?:\/\//i.test(value) ? value : `https://${value}`;
}
